import React, { useState } from "react";
import {
	Button,
	CircularProgress,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	TextField,
} from "@mui/material";
import { ResponsiveDialog } from "components";
import { TaskBoardFilterStatus, TaskBoardState, TaskHeaderStats, TaskItemViewModel, TaskStatusCounts } from "../types";
import { TaskRootStatus, TeamLeaderServices } from "shared/services";
import { NotistackServices } from "shared/services";
import styles from "../styles.module.scss";
import { TaskItemCard } from "./TaskItemCard";

interface TaskDetailProps {
	selectedTaskObjective: string;
	selectedTaskStatus: TaskRootStatus;
	selectedTaskCancellation: { cancelledAt: string; reason: string | null } | null;
	headerStats: TaskHeaderStats;
	statusCounts: TaskStatusCounts;
	statusFilter: TaskBoardFilterStatus;
	onStatusFilterChange: (next: TaskBoardFilterStatus) => void;
	items: TaskItemViewModel[];
	isLoading: boolean;
	workspaceId?: string | null;
	selectedTaskId?: string | null;
	state?: TaskBoardState;
	onCancelled?: () => void;
}

export const TaskDetail: React.FC<TaskDetailProps> = ({
	selectedTaskObjective,
	selectedTaskStatus,
	selectedTaskCancellation,
	headerStats,
	statusCounts,
	statusFilter,
	onStatusFilterChange,
	items,
	isLoading,
	workspaceId,
	selectedTaskId,
	state,
	onCancelled,
}) => {
	const filters: Array<{ key: TaskBoardFilterStatus; label: string; count: number }> = [
		{ key: "all", label: "Todas", count: statusCounts.all },
		{ key: "pending", label: "Pendente", count: statusCounts.pending },
		{ key: "processing", label: "Executando", count: statusCounts.processing },
		{ key: "completed", label: "Concluída", count: statusCounts.completed },
		{ key: "failed", label: "Erro", count: statusCounts.failed },
		{ key: "paused", label: "Pausada", count: statusCounts.paused },
	];

	const [cancelOpen, setCancelOpen] = useState(false);
	const [cancelReason, setCancelReason] = useState("");
	const [cancelling, setCancelling] = useState(false);
	const canCancel = selectedTaskStatus === "active" && Boolean(workspaceId && selectedTaskId);

	const handleConfirmCancel = async () => {
		if (!workspaceId || !selectedTaskId) return;
		setCancelling(true);
		try {
			await TeamLeaderServices.cancelTask(workspaceId, selectedTaskId, cancelReason.trim() || undefined);
			NotistackServices.showSuccessMessage("Tarefa cancelada. Subtarefas em andamento serão interrompidas.");
			setCancelOpen(false);
			setCancelReason("");
			onCancelled?.();
		} catch (err: any) {
			const msg = err?.response?.data?.message ?? err?.message ?? "Não foi possível cancelar a tarefa.";
			NotistackServices.showErrorMessage(msg);
		} finally {
			setCancelling(false);
		}
	};

	return (
		<section className={styles.detailContainer}>
			<header className={styles.detailHeader}>
				<div style={{ display: "flex", flexDirection: "column", justifyContent: "space-around" }}>
					<h1 className={styles.detailTitle}>Minhas tarefas</h1>
					{selectedTaskObjective && (
						<p className={styles.detailTaskObjective}>
							{selectedTaskObjective.slice(0, 150)}
							{selectedTaskObjective.length > 150 ? "..." : ""}
						</p>
					)}
					{selectedTaskStatus === "cancelled" && selectedTaskCancellation && (
						<p className={styles.detailTaskObjective} style={{ color: "var(--mui-palette-warning-main)" }}>
							🛑 Cancelada em {new Date(selectedTaskCancellation.cancelledAt).toLocaleString("pt-BR")}
							{selectedTaskCancellation.reason ? ` — Motivo: ${selectedTaskCancellation.reason}` : ""}
						</p>
					)}
				</div>

				<div className={styles.detailHeaderRight}>
					<div className={styles.metricsRow}>
						<div className={styles.metricItem}>
							<span className={styles.metricLabel}>REALIZADAS HOJE</span>
							<strong className={styles.metricCompleted}>{headerStats.completedToday}</strong>
						</div>
						<div className={styles.metricItem}>
							<span className={styles.metricLabel}>EM ANDAMENTO</span>
							<strong className={styles.metricProcessing}>{headerStats.processing}</strong>
						</div>
						<div className={styles.metricItem}>
							<span className={styles.metricLabel}>AGUARDANDO</span>
							<strong className={styles.metricWaiting}>{headerStats.waiting}</strong>
						</div>
						<div className={styles.metricItem}>
							<span className={styles.metricLabel}>ERROS</span>
							<strong className={styles.metricFailed}>{headerStats.failed}</strong>
						</div>
					</div>

					{canCancel && (
						<Button
							variant="outlined"
							color="warning"
							size="small"
							onClick={() => setCancelOpen(true)}
							sx={{ alignSelf: "flex-end", textTransform: "none", fontWeight: 700 }}
						>
							Cancelar tarefa
						</Button>
					)}
				</div>
			</header>

			<div className={styles.detailToolbar}>
				<div className={styles.filterRow}>
					{filters.map((filter) => (
						<button
							key={filter.key}
							type="button"
							onClick={() => onStatusFilterChange(filter.key)}
							className={`${styles.statusFilterChip} ${statusFilter === filter.key ? styles.statusFilterChipActive : ""}`}
						>
							{filter.label}
							<span>{filter.count}</span>
						</button>
					))}
				</div>
			</div>

			<div className={styles.itemsContainer}>
				{isLoading ? (
					<div className={styles.loadingWrap}>
						<CircularProgress size={20} />
						<span>Sincronizando tasks em tempo real...</span>
					</div>
				) : items.length === 0 ? (
					<div className={styles.emptyWrap}>Nenhum task item encontrado para os filtros atuais.</div>
				) : (
					items.map((item) => (
						<TaskItemCard
							key={item.id}
							item={item}
							workspaceId={workspaceId}
							taskId={selectedTaskId}
							state={state}
						/>
					))
				)}
			</div>

			<footer className={styles.detailFooterNote}>
				Atualizações emitidas via stream SSE a cada transição da state machine: pendente, executando, concluída, erro e cancelada.
			</footer>

			<ResponsiveDialog open={cancelOpen} onClose={() => !cancelling && setCancelOpen(false)} maxWidth="sm" fullWidth>
				<DialogTitle>Cancelar esta tarefa?</DialogTitle>
				<DialogContent>
					<DialogContentText sx={{ mb: 2 }}>
						As subtarefas pendentes serão marcadas como canceladas e nenhum novo ciclo será disparado.
						<br />
						<br />
						<strong>Importante:</strong> esta ação NÃO desfaz e-mails, eventos ou mensagens já enviadas, e NÃO interrompe uma execução de IA que esteja no meio do processamento (ela termina e o resultado é descartado).
					</DialogContentText>
					<TextField
						label="Motivo (opcional)"
						placeholder="Ex.: enviei na pessoa errada, vou refazer o pedido..."
						multiline
						minRows={2}
						maxRows={4}
						fullWidth
						value={cancelReason}
						onChange={(e) => setCancelReason(e.target.value)}
						disabled={cancelling}
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setCancelOpen(false)} disabled={cancelling} color="inherit">
						Voltar
					</Button>
					<Button onClick={handleConfirmCancel} disabled={cancelling} color="warning" variant="contained">
						{cancelling ? "Cancelando..." : "Confirmar cancelamento"}
					</Button>
				</DialogActions>
			</ResponsiveDialog>
		</section>
	);
};
